/**
 * Ficha de visita em A4 — porte fiel do `buildPrintHTML` do sistema 4.x.
 * A paleta suave é intencional: distribui o desgaste dos cartuchos C/M/Y.
 */
import { LOGO } from "./logo";
import {
  descontoAntecipado,
  descontoCondicaoExclusiva,
  valorAposVencimento,
  PCT_ANTECIPADO_PADRAO,
} from "./calculos";
import { dataHora, data as fmtData, dataNasc } from "./formato";
import { telefoneCompleto } from "./paises";
import { ROTULO_PERIODO, ROTULO_STATUS, type Diferencial, type StatusVisita, type Visita } from "./tipos";

/** Quanto tempo a proposta vale, em dias corridos contados da emissão. */
const VALIDADE_DIAS = 10;

const cur = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "R$ 0,00";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Escapa o que vem do usuário — a ficha é montada por concatenação de HTML. */
const esc = (v: unknown): string =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const ou = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : esc(v));

const ESTILO = `
  @page { size: A4; margin: 10mm 11mm 8mm 11mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Tahoma, Geneva, sans-serif; color: #2b2b2b; font-size: 10.5px; line-height: 1.32; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 100%; }
  .header { display: flex; align-items: center; padding-bottom: 6px; border-bottom: 2px solid #4f9a6a; margin-bottom: 5px; }
  .header img { width: 46px; height: auto; margin-right: 12px; }
  .header .info { flex: 1; }
  .header .info h1 { font-size: 16px; color: #215c3a; font-weight: 700; letter-spacing: .3px; }
  .header .info .slogan { font-size: 10px; color: #4f9a6a; font-weight: 600; font-style: italic; }
  .header .info p { font-size: 8.5px; color: #6b6b6b; }
  .header .meta { text-align: right; font-size: 9px; color: #555; line-height: 1.45; }
  .header .meta strong { color: #333; }
  .subtitle { text-align: center; background: #dfefe4; color: #215c3a; border: 1px solid #4f9a6a; font-size: 11.5px; font-weight: 700; padding: 4px 0; letter-spacing: 1.2px; text-transform: uppercase; border-radius: 3px; }
  .greet { text-align: center; font-size: 11px; color: #3d3d3d; font-weight: 600; margin: 5px 0 7px 0; }
  .section { margin-bottom: 6px; break-inside: avoid; page-break-inside: avoid; }
  .section-title { font-size: 9.5px; font-weight: 700; padding: 3px 8px; text-transform: uppercase; letter-spacing: .8px; border-radius: 2px; margin-bottom: 3px; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  td { padding: 2.5px 6px; border: 1px solid #d6d6d6; vertical-align: top; font-size: 9.8px; word-wrap: break-word; }
  .lbl { font-weight: 600; }
  .val { color: #333; }
  .grid2 .lbl { width: 26%; } .grid2 .val { width: 24%; }
  .vert .lbl { width: 54%; } .vert .val { width: 46%; }
  .bold { font-weight: 700; }
  .highlight { font-weight: 700 !important; }
  .cols { display: flex; gap: 8px; align-items: flex-start; }
  .col { flex: 1 1 0; min-width: 0; }
  .obs-box { border: 1px solid #d6d6d6; border-radius: 3px; padding: 5px 8px; font-size: 9.5px; color: #333; }
  .doc-list { display: flex; flex-wrap: wrap; gap: 1px 12px; }
  .doc-list div { flex: 0 0 48%; font-size: 9.5px; }
  .validity { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #bbb; font-weight: 700; font-size: 9.5px; color: #215c3a; }
  .dif-grid { display: flex; flex-wrap: wrap; gap: 2px 10px; padding: 2px 0; }
  .dif-item { display: flex; align-items: center; gap: 4px; font-size: 9.3px; font-weight: 600; flex: 0 0 31%; }
  .dif-check { width: 11px; height: 11px; border-radius: 2px; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 700; flex-shrink: 0; }
  .thanks { margin-top: 7px; padding: 7px 10px; background: #fdeee6; border-left: 3px solid #d38357; font-size: 9.6px; line-height: 1.45; color: #8c4a24; break-inside: avoid; }
  .thanks p { margin-bottom: 4px; }
  .thanks .tit { font-weight: 700; font-size: 10.5px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 26px; padding: 0 20px; break-inside: avoid; }
  .sig-block { text-align: center; width: 210px; }
  .sig-block .line { border-top: 1px solid #444; margin-bottom: 3px; }
  .sig-block .name { font-size: 9px; color: #555; }
  .footer { margin-top: 10px; padding-top: 6px; border-top: 1px solid #ccc; text-align: center; font-size: 8.2px; color: #8a8a8a; }
  .footer span { color: #215c3a; font-weight: 600; }
  .s-verde .section-title { background:#dfefe4; color:#215c3a; border-left:3px solid #4f9a6a; }
  .s-verde .lbl, .s-verde .highlight { background:#eaf4ed; color:#215c3a; }
  .s-verde .dif-check { background:#4f9a6a; }
  .s-teal .section-title { background:#e0eff1; color:#175b66; border-left:3px solid #3f97a4; }
  .s-teal .lbl, .s-teal .highlight { background:#eaf3f4; color:#175b66; }
  .s-teal .dif-check { background:#3f97a4; }
  .s-azul .section-title { background:#e3e9f7; color:#2c4a8c; border-left:3px solid #5b7cc4; }
  .s-azul .lbl, .s-azul .highlight { background:#edf0f9; color:#2c4a8c; }
  .s-azul .dif-check { background:#5b7cc4; }
  .s-lilas .section-title { background:#e3d8f4; color:#553c8b; border-left:3px solid #8a6fc0; }
  .s-lilas .lbl, .s-lilas .highlight { background:#efe9f8; color:#553c8b; }
  .s-lilas .dif-check { background:#8a6fc0; }
  .s-rosa .section-title { background:#f4d3e0; color:#8d3b5c; border-left:3px solid #c76a92; }
  .s-rosa .lbl, .s-rosa .highlight { background:#f9e4ec; color:#8d3b5c; }
  .s-rosa .dif-check { background:#c76a92; }
  .s-ambar .section-title { background:#fae6ca; color:#8a5717; border-left:3px solid #d3903c; }
  .s-ambar .lbl, .s-ambar .highlight { background:#fdf1e1; color:#8a5717; }
  .s-ambar .dif-check { background:#d3903c; }
`;

/**
 * Monta a folha A4 entregue à família.
 *
 * Serve aos dois casos com o mesmo desenho:
 *
 * - **Visita** — o papel entregue no balcão, no fim do atendimento.
 * - **Orçamento** — o PDF mandado pelo WhatsApp para quem não veio à escola.
 *   Some o bloco "Informações da Visita" (não houve visita), a assinatura
 *   (ninguém assina um arquivo) e o agradecimento pela visita; entram a
 *   validade da proposta e o convite para conhecer a escola.
 *
 * `catalogo` é a lista completa de diferenciais da escola. No orçamento a
 * família não ouviu a apresentação, então a folha precisa apresentar a escola
 * inteira — os itens escolhidos para aquele aluno saem marcados, o resto sai
 * em cinza. Sem catálogo, o comportamento é o de sempre: só os marcados.
 */
export function montarFicha(v: Visita, catalogo?: Diferencial[]): string {
  const orc = v.tipo === "ORCAMENTO";
  const p = v.proposta;
  const proto = v.id.slice(0, 8).toUpperCase();
  const responsavel = v.nomeResponsavel || "Sr(a). Responsável";

  const escolhidos = new Set(v.diferenciais.map((d) => d.diferencial.nome));
  /** Itens de um tipo: no orçamento, o catálogo inteiro; senão, só os marcados. */
  const doTipo = (tipo: "GRADE" | "INTEGRAL") =>
    orc && catalogo?.length
      ? catalogo.filter((d) => d.tipo === tipo).map((d) => ({ nome: d.nome, marcado: escolhidos.has(d.nome) }))
      : v.diferenciais
          .filter((d) => d.diferencial.tipo === tipo)
          .map((d) => ({ nome: d.diferencial.nome, marcado: true }));

  const grade = doTipo("GRADE");
  const integral = doTipo("INTEGRAL");

  const listaDif = (itens: Array<{ nome: string; marcado: boolean }>) =>
    itens
      .map(({ nome, marcado }) =>
        marcado
          ? `<div class="dif-item"><div class="dif-check">&#10003;</div>${esc(nome)}</div>`
          : `<div class="dif-item" style="opacity:.62"><div class="dif-check" style="background:#fff;border:1px solid #bbb;color:#bbb">&#9679;</div>${esc(nome)}</div>`,
      )
      .join("");

  /**
   * Numeração corrida das seções. Era fixa (1..9) e quebrava no orçamento, que
   * não tem o bloco da visita: a folha saía "1, 2, 4, 5..." com um buraco.
   */
  let secao = 0;
  const n = () => ++secao;

  const emissao = new Date();
  const validade = new Date(emissao.getTime() + VALIDADE_DIAS * 24 * 60 * 60 * 1000);

  /**
   * As duas faixas do desconto, para a ficha ler como um recibo: cada linha
   * subtrai da anterior. O percentual não aparece de propósito — é ele que
   * convida a família a calcular o tamanho da perda por atrasar.
   */
  const faixas = (normal: unknown, pct: unknown, comDesc: unknown, pctAnt: unknown) => {
    const antecipado = descontoAntecipado(normal, pct, pctAnt);
    const boleto = valorAposVencimento(comDesc, antecipado);
    return {
      exclusiva: descontoCondicaoExclusiva(normal, comDesc, antecipado),
      antecipado,
      boleto,
    };
  };

  /** Linha de abatimento: sempre com o sinal, para ficar claro que subtrai. */
  const menos = (v: string) => (v ? `− ${cur(v)}` : cur(0));

  const blocoValores = (titulo: string, classe: string, campos: {
    normal: unknown; pct: unknown; comDesc: unknown; parcelas: unknown;
    matricula: unknown; pctMat: unknown; matComDesc: unknown; anual: unknown;
    pctAnt: unknown;
  }) => {
    const m = faixas(campos.normal, campos.pct, campos.comDesc, campos.pctAnt);
    const mat = faixas(campos.matricula, campos.pctMat, campos.matComDesc, campos.pctAnt);
    return `
    <div class="col section ${classe}">
      <div class="section-title">${titulo}</div>
      <table class="vert">
        <tr><td class="lbl">Mensalidade</td><td class="val">${cur(campos.normal)}</td></tr>
        <tr><td class="lbl">Condição exclusiva</td><td class="val">${esc(menos(m.exclusiva))}</td></tr>
        <tr><td class="lbl">Valor do boleto</td><td class="val bold">${cur(m.boleto)}</td></tr>
        <tr><td class="lbl">Benefício pagamento antecipado</td><td class="val">${esc(menos(m.antecipado))}</td></tr>
        <tr><td class="lbl">Pagando até a data de vencimento</td><td class="val bold">${cur(campos.comDesc)}</td></tr>
        <tr><td class="lbl">Qtde Parcelas</td><td class="val">${esc(campos.parcelas || "0")}</td></tr>
        <tr><td class="lbl">Matrícula</td><td class="val">${cur(campos.matricula)}</td></tr>
        <tr><td class="lbl">Condição exclusiva</td><td class="val">${esc(menos(mat.exclusiva))}</td></tr>
        <tr><td class="lbl">Boleto da matrícula</td><td class="val">${cur(mat.boleto)}</td></tr>
        <tr><td class="lbl">Benefício pagamento antecipado</td><td class="val">${esc(menos(mat.antecipado))}</td></tr>
        <tr><td class="lbl">Matrícula pagando até o vencimento</td><td class="val bold">${cur(campos.matComDesc)}</td></tr>
        <tr><td class="lbl">Investimento Anual Estimado</td><td class="val highlight">${cur(campos.anual)}</td></tr>
      </table>
    </div>`;
  };

  // o recado que a família deixou no site é observação também — e é o que
  // ninguém da equipe digitou, então some da ficha se não for impresso aqui
  /**
   * Parcelamento do material na ficha: só o número de parcelas combinado.
   *
   * O percentual de acréscimo saiu daqui de propósito. Ele é instrumento de
   * negociação do consultor, que continua vendo a faixa na tela; impresso no
   * papel que a família leva para casa, vira conversa sobre juros em vez de
   * conversa sobre a escola. A régua completa ("até 3x sem acréscimo · …")
   * também saiu, pelo mesmo motivo.
   */
  const parcelamentoMaterial = p?.qtdParcelasMaterial ? `${esc(p.qtdParcelasMaterial)}x` : "—";

  const temObs = Boolean(v.descricao || v.observacoes);
  const titulo = orc ? "Proposta de Investimento" : "Ficha de Visita";

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"/>
<title>${esc(titulo)} — ${esc(v.nomeAluno)}</title><style>${ESTILO}</style></head><body>
<div class="page">

  <div class="header">
    <img src="${LOGO}" alt="COC"/>
    <div class="info">
      <h1>Colégio COCSJC — Unidade SUL</h1>
      <div class="slogan">Sua melhor opção em BILÍNGUE</div>
      <p>${orc ? "Proposta de investimento para a família" : "Sistema de Gerenciamento de Visitas"}</p>
    </div>
    <div class="meta">
      ${orc
        ? `<div><strong>Emitida em:</strong> ${esc(fmtData(emissao.toISOString()))}</div>
           <div><strong>Protocolo:</strong> ${esc(proto)}</div>
           <div><strong>Válida até:</strong> ${esc(fmtData(validade.toISOString()))}</div>`
        : `<div><strong>Data:</strong> ${esc(fmtData(v.inicioEm))}</div>
           <div><strong>Protocolo:</strong> ${esc(proto)}</div>
           <div><strong>Horário:</strong> ${esc(dataHora(v.inicioEm))}</div>`}
    </div>
  </div>

  <div class="subtitle">${esc(titulo)}</div>
  <div class="greet">Olá, ${esc(responsavel)}!</div>

  <div class="cols">
    <div class="col section s-teal">
      <div class="section-title">${n()}. Dados do ${orc ? "Responsável" : "Visitante / Responsável"}</div>
      <table class="vert">
        <tr><td class="lbl">Nome do ${orc ? "Responsável" : "Visitante / Responsável"}</td><td class="val">${ou(v.nomeResponsavel)}</td></tr>
        <tr><td class="lbl">Telefone para Contato</td><td class="val">${esc(telefoneCompleto(v.telefoneResponsavel, v.paisTelefone))}</td></tr>
        <tr><td class="lbl">Período Pretendido</td><td class="val">${ou(v.periodo ? ROTULO_PERIODO[v.periodo] : "")}</td></tr>
        <tr><td class="lbl">E-mail</td><td class="val">${ou(v.emailResponsavel)}</td></tr>
      </table>
    </div>
    <div class="col section s-rosa">
      <div class="section-title">${n()}. Dados do Aluno</div>
      <table class="vert">
        <tr><td class="lbl">Nome do Aluno(a)</td><td class="val">${ou(v.nomeAluno)}</td></tr>
        <tr><td class="lbl">Data de Nascimento</td><td class="val">${ou(dataNasc(v.dataNascimento))}</td></tr>
        <tr><td class="lbl">Curso Pretendido</td><td class="val">${ou(v.curso?.nome)}</td></tr>
        <tr><td class="lbl">Série Pretendida</td><td class="val">${ou(v.serie?.nome)}</td></tr>
        <tr><td class="lbl">Nome do Irmão(ã)</td><td class="val">${ou(v.nomeIrmao)}</td></tr>
        <tr><td class="lbl">Série do Irmão(ã)</td><td class="val">${ou(v.serieIrmao)}</td></tr>
      </table>
    </div>
  </div>

  ${orc ? "" : `
  <div class="section s-ambar">
    <div class="section-title">${n()}. Informações da Visita</div>
    <table class="grid2">
      <tr><td class="lbl">Data/Hora Início</td><td class="val">${esc(dataHora(v.inicioEm))}</td><td class="lbl">Data/Hora Término</td><td class="val">${esc(dataHora(v.fimEm))}</td></tr>
      <tr><td class="lbl">Indicação</td><td class="val">${ou(v.indicacao)}</td><td class="lbl">Onde Conheceu</td><td class="val">${ou(v.ondeConheceu)}</td></tr>
      <tr><td class="lbl">Status</td><td class="val bold" colspan="3">${ou(ROTULO_STATUS[v.status as StatusVisita])}</td></tr>
    </table>
  </div>`}

  ${(() => { const i = n(); return `
  <div class="cols">
    ${blocoValores(`${i}a. Valores — Período Parcial`, "s-verde", {
      normal: p?.valorNormal, pct: p?.percentualDesconto, comDesc: p?.valorComDesconto,
      parcelas: p?.qtdParcelas, matricula: p?.valorMatricula, pctMat: p?.percentualDescontoMatricula,
      matComDesc: p?.valorMatriculaComDesconto, anual: p?.valorAnual,
      pctAnt: p?.percentualAntecipado ?? PCT_ANTECIPADO_PADRAO,
    })}
    ${blocoValores(`${i}b. Valores — Período Integral`, "s-azul", {
      normal: p?.valorNormalIntegral, pct: p?.percentualDescontoIntegral, comDesc: p?.valorComDescontoIntegral,
      parcelas: p?.qtdParcelasIntegral, matricula: p?.valorMatriculaIntegral, pctMat: p?.percentualDescontoMatriculaIntegral,
      matComDesc: p?.valorMatriculaComDescontoIntegral, anual: p?.valorAnualIntegral,
      pctAnt: p?.percentualAntecipado ?? PCT_ANTECIPADO_PADRAO,
    })}
  </div>`; })()}

  <div class="section s-lilas">
    <div class="section-title">${n()}. Material Didático</div>
    <table class="grid2">
      <tr><td class="lbl">Material COC</td><td class="val">${cur(p?.materialCoc)}</td><td class="lbl">Material Bilíngue</td><td class="val">${cur(p?.materialBilingue)}</td></tr>
      <tr><td class="lbl">Total do Material</td><td class="val bold">${cur(p?.totalMaterial)}</td><td class="lbl">Parcelamento</td><td class="val">${parcelamentoMaterial}</td></tr>
      <tr><td class="lbl">Valor da Parcela</td><td class="val highlight" colspan="3">${cur(p?.valorParcelaMaterial)}</td></tr>
    </table>
  </div>

  ${grade.length ? `
  <div class="section s-teal">
    <div class="section-title">${n()}. Diferenciais da Grade Curricular — COCSJC Unidade Sul</div>
    <div class="dif-grid">${listaDif(grade)}</div>
  </div>` : ""}

  ${integral.length ? `
  <div class="section s-rosa">
    <div class="section-title">${n()}. Diferenciais do Período Integral — COCSJC Unidade Sul</div>
    <div class="dif-grid">${listaDif(integral)}</div>
  </div>` : ""}

  ${temObs ? `
  <div class="section s-azul">
    <div class="section-title">${n()}. Observações</div>
    ${v.observacoes ? `<div class="obs-box" style="white-space:pre-wrap"><strong>${v.origemPublica ? "Recado da família no agendamento pelo site" : "Comentário registrado no sistema anterior"}:</strong><br/>${esc(v.observacoes)}</div>` : ""}
    ${v.descricao ? `<div class="obs-box" style="white-space:pre-wrap">${esc(v.descricao)}</div>` : ""}
  </div>` : ""}

  <div class="section s-verde">
    <div class="section-title">${n()}. Documentos Necessários para Matrícula</div>
    <div class="obs-box">
      <div class="doc-list">
        <div>• Documentos pessoais dos responsáveis pelo aluno</div>
        <div>• Certidão de nascimento do aluno e RG</div>
        <div>• Comprovante de endereço</div>
        <div>• Carteira de vacinação</div>
        <div style="flex:0 0 100%">• Em caso de transferência de outra escola particular: comprovante de quitação da escola de origem</div>
      </div>
      <div class="validity">⚠ ${orc
        ? `Esta proposta é válida até ${esc(fmtData(validade.toISOString()))} (${VALIDADE_DIAS} dias corridos).`
        : `Este orçamento tem validade de ${VALIDADE_DIAS} dias corridos.`}</div>
    </div>
  </div>

  ${orc ? `
  <div class="thanks">
    <p class="tit">Que tal conhecer a escola de perto, ${esc(responsavel)}?</p>
    <p>Esta proposta foi preparada especialmente para ${esc(v.nomeAluno)}. Nada substitui, porém, andar pelos corredores, ver as salas e conversar com quem cuida do dia a dia dos nossos alunos. Responda a esta mensagem que agendamos a sua visita no horário que for melhor para a sua família — sem compromisso.</p>
    <p style="margin-bottom:0;font-style:italic">Atenciosamente,<br/><strong>Equipe COCSJC Unidade Sul</strong></p>
  </div>` : `
  <div class="thanks">
    <p class="tit">Obrigado pela sua visita, ${esc(responsavel)}!</p>
    <p>Foi um prazer recebê-lo(a) no COCSJC Unidade Sul. Em breve enviaremos uma breve pesquisa de satisfação pelo WhatsApp — sua opinião é muito importante para nós e nos ajudará a melhorar continuamente o atendimento às famílias.</p>
    <p style="margin-bottom:0;font-style:italic">Atenciosamente,<br/><strong>Equipe COCSJC Unidade Sul</strong></p>
  </div>

  <div class="signatures">
    <div class="sig-block"><div class="line"></div><div class="name">Visitante / Responsável</div></div>
    <div class="sig-block"><div class="line"></div><div class="name">Atendente COC</div></div>
  </div>`}

  <div class="footer">
    <span>Colégio COCSJC — Unidade SUL</span> — ${esc(titulo)} — Protocolo ${esc(proto)} — Documento gerado em ${esc(dataHora(emissao.toISOString()))}<br/>
    ${orc
      ? "Documento preparado para a família. Os valores podem ser revistos após o prazo de validade."
      : "Este documento é de uso interno e para entrega ao visitante."}
  </div>

</div></body></html>`;
}
